import { FC, ReactNode } from 'react';
import { FormAsyncSelectField, FormAsyncSelectFieldProps, lt, useToggle } from '@mews/framework';
import { Customer } from '@mews-commander/data-access';
import { Button, Container, Dialog, DropdownItem, IconName, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useCommanderSelector } from '@mews-commander/utils';
import { isCurrentProfileEnabledToManageCustomers } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { ProfileCreateForm } from '../../../../../modules/customerProfile/components';
import { useAsyncSelectFieldControls } from '../useAsyncSelectFieldControls';
import { useSearchCustomersQuery } from './useSearchCustomersQuery';
import { useGetAllCustomersQuery } from './useGetAllCustomersQuery';

const DROPDOWN_PORTAL_CONTAINER_ID = 'customer-search-dropdown-portal';

const renderData = (customers: Customer[]): React.ReactNode =>
    customers.map(({ id, email, firstName, lastName }) => (
        <DropdownItem
            key={id}
            value={id}
            description={email}
            label={
                <>
                    {lastName}
                    {UnicodeEntity.NoBreakSpace}
                    {firstName}
                </>
            }
        />
    ));

interface CustomerProfileSearchFieldProps
    extends Omit<FormAsyncSelectFieldProps<Customer>, 'useDataQuery' | 'renderData'> {
    newCustomerButtonText?: ReactNode;
    initialEmptyMessage?: ReactNode;
}

export const CustomerProfileSearchField: FC<CustomerProfileSearchFieldProps> = ({
    name,
    multiselect,
    newCustomerButtonText,
    ...others
}: CustomerProfileSearchFieldProps) => {
    const enterpriseId = useCurrentEnterpriseId();

    const canManageCustomers: boolean = useCommanderSelector((state) =>
        isCurrentProfileEnabledToManageCustomers(state)
    );

    const [active, toggleDialog] = useToggle();

    const { selectedItems, addSelectedItem, ...controls } = useAsyncSelectFieldControls<Customer>({
        useSearchQuery: useSearchCustomersQuery,
        useGetDefaultQuery: useGetAllCustomersQuery,
        name,
        multiselect,
    });

    return (
        <>
            <Container id={`${DROPDOWN_PORTAL_CONTAINER_ID}-${name}`}>
                <FormAsyncSelectField<Customer[]>
                    clearable
                    name={name}
                    multiselect={multiselect}
                    label={lt('Customer')}
                    placeholder={lt('SearchByName')}
                    emptyMessage={
                        <Typography textStyle="bodyMediumStrong" variant="tertiary" as="div">
                            {lt('NoResultsForSearch')}
                        </Typography>
                    }
                    renderData={renderData}
                    defaultOptions={renderData(selectedItems)}
                    footer={
                        canManageCustomers && newCustomerButtonText
                            ? {
                                  primary: <></>,
                                  secondary: (
                                      <Button
                                          type="button"
                                          size="small"
                                          icon={IconName.PlusSimple}
                                          variant="ghost"
                                          onClick={toggleDialog}
                                      >
                                          <Typography textStyle="bodyMediumStrong" as="div">
                                              {newCustomerButtonText}
                                          </Typography>
                                      </Button>
                                  ),
                              }
                            : undefined
                    }
                    portalContainerId={`${DROPDOWN_PORTAL_CONTAINER_ID}-${name}`}
                    {...controls}
                    {...others}
                />
            </Container>
            <Dialog active={active} onClose={toggleDialog} title={lt(TextKey.AddNewCustomer)}>
                <ProfileCreateForm
                    withLoader
                    form="add-customer-form"
                    buttonVariant="tertiary"
                    enterpriseId={enterpriseId}
                    lastName=""
                    onSuccess={(customer) => {
                        addSelectedItem(customer);
                        toggleDialog();
                    }}
                />
            </Dialog>
        </>
    );
};
